import { Injectable, inject } from '@angular/core';
import { Observable, catchError, of, tap, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseAsyncService } from '@core/state/base-service';
import { NotificationsStore } from '@core/state/notifications.store';
import {
  AlertDto,
  HasNotificationDto,
  AlertCountsDto,
  AlertFolderCountsDto,
  AlertCategory,
  AlertSeverity
} from '@shared/models/dto/api-dtos';
import {
  CreateAlertRequest,
  AlertDtoApiPaginated,
  Notification as AppNotification
} from '@shared/models/notification/notification.models';

/**
 * Alerts Service
 *
 * Handles all API calls related to alerts/notifications.
 * Manages alert creation, retrieval, marking as read, and dismissal.
 * Integrates with NotificationsStore for state management.
 *
 * Features:
 * - Create alerts
 * - Get alerts with filtering
 * - Get alert by ID
 * - Check if file has notifications
 * - Get unread alerts for a file
 * - Paginated search of unread alerts
 * - Mark alert as read
 * - Dismiss/delete alert
 */
export interface AlertsFiltersParams {
  onlyUnread?: boolean;
  includeDismissed?: boolean;
  successionFileId?: string;
  category?: AlertCategory;
  severity?: AlertSeverity;
}
@Injectable({ providedIn: 'root' })

export class AlertsService extends BaseAsyncService {
  private readonly notificationsStore = inject(NotificationsStore);
  private readonly BASE_ENDPOINT = '/alerts';

  /**
   * Request configuration for API calls
   */
  private readonly requestConfig = {
    retry: this.createRetryConfig(0, 800, 1.5),
    logErrors: true
  };

  /**
   * Creates a new alert and notifies via SignalR
   * @param request Create alert request
   * @returns Observable of created AlertDto
   */
  createAlert(request: CreateAlertRequest): Observable<AlertDto> {
    this.logger.info('[AlertsService] Creating alert', { request });

    return this.post<AlertDto>(
      this.BASE_ENDPOINT,
      request,
      this.requestConfig
    ).pipe(
      tap((alert) => {
        this.logger.info('[AlertsService] Alert created successfully', { alertId: alert.id });
        // The alert will be received via SignalR and added to store automatically
      }),
      catchError((error) => {
        this.logger.error('[AlertsService] Failed to create alert', error);
        throw error;
      })
    );
  }

  /**
   * Gets all alerts for the current user with optional filters
   * Updates the notifications store with the fetched alerts
   * @param filters Filter options
   * @param updateStore Whether to update the store (default: true)
   * @returns Observable of AlertDto array
   */
  getAlerts(filters?: AlertsFiltersParams, updateStore: boolean = true): Observable<AlertDto[]> {
    this.logger.info('[AlertsService] Getting alerts', { filters, updateStore });

    if (updateStore) {
      this.notificationsStore.setLoading(true);
      this.notificationsStore.setError(null);
    }

    const queryParams = this.buildQueryParams(filters);
    const endpoint = `${this.BASE_ENDPOINT}${queryParams}`;

    return this.get<AlertDto[]>(endpoint, this.requestConfig).pipe(
      tap((alerts) => {
        this.logger.info('[AlertsService] Alerts retrieved', { count: alerts.length });

        if (updateStore) {
          // Update store with alerts
          alerts.forEach(alert => {
            const notification = this.mapAlertDtoToNotification(alert);
            // Only add if not already in store, or update if it exists
            const existing = this.notificationsStore.getNotificationById(notification.id);
            if (!existing) {
              this.notificationsStore.addNotification(notification);
            } else {
              // Update existing notification (e.g., if readAt changed)
              this.updateNotificationInStore(notification);
            }
          });
          this.notificationsStore.setLoading(false);
        }
      }),
      catchError((error) => {
        this.logger.error('[AlertsService] Failed to get alerts', error);
        if (updateStore) {
          this.notificationsStore.setError('Impossible de charger les notifications');
          this.notificationsStore.setLoading(false);
        }
        return of([]);
      })
    );
  }

  /**
   * Loads initial notifications on app startup or when user logs in
   * Fetches unread notifications and updates the store
   * @returns Observable of AlertDto array
   */
  loadInitialNotifications(): Observable<AlertDto[]> {
    this.logger.info('[AlertsService] Loading initial notifications');
    return this.getAlerts({ onlyUnread: true }, true);
  }

  /**
   * Refreshes all notifications from the server
   * Useful for manual refresh or after connection issues
   * @returns Observable of AlertDto array
   */
  refreshNotifications(): Observable<AlertDto[]> {
    this.logger.info('[AlertsService] Refreshing notifications');
    return this.getAlerts({}, true);
  }

  /**
   * Gets an alert by its ID
   * Updates the store with the fetched alert
   * @param id Alert ID (UUID)
   * @returns Observable of AlertDto
   */
  getAlertById(id: string): Observable<AlertDto | null> {
    this.logger.info('[AlertsService] Getting alert by ID', { id });

    const endpoint = `${this.BASE_ENDPOINT}/${id}`;

    return this.get<AlertDto>(endpoint, this.requestConfig).pipe(
      tap((alert) => {
        this.logger.info('[AlertsService] Alert retrieved', { alertId: alert.id });
        // Update store with alert
        const notification = this.mapAlertDtoToNotification(alert);
        const existing = this.notificationsStore.getNotificationById(notification.id);
        if (!existing) {
          this.notificationsStore.addNotification(notification);
        } else {
          // Update existing notification
          this.updateNotificationInStore(notification);
        }
      }),
      catchError((error) => {
        this.logger.error('[AlertsService] Failed to get alert by ID', { id, error });
        return of(null);
      })
    );
  }

  /**
   * Checks if a succession file has unread notifications for the current user
   * @param successionFileId Succession file ID
   * @returns Observable of HasNotificationDto
   */
  hasNotification(successionFileId: number): Observable<HasNotificationDto> {
    this.logger.info('[AlertsService] Checking if file has notifications', { successionFileId });

    const endpoint = `${this.BASE_ENDPOINT}/succession-file/${successionFileId}/has-notification`;

    return this.get<HasNotificationDto>(endpoint, this.requestConfig).pipe(
      tap((result) => {
        this.logger.info('[AlertsService] Has notification result', {
          successionFileId,
          hasNotification: result.hasNotification,
          unreadCount: result.unreadCount
        });
      }),
      catchError((error) => {
        this.logger.error('[AlertsService] Failed to check notifications', { successionFileId, error });
        // Return default value on error
        return of({
          successionFileId,
          hasNotification: false,
          unreadCount: 0
        });
      })
    );
  }

  /**
   * Gets alert counts grouped by category for a specific succession file
   * @param successionFileId Succession file ID
   * @returns Observable of AlertCountsDto array
   */
  getAllFoldersAlertCounts(): Observable<AlertFolderCountsDto[]> {
    this.logger.info('[AlertsService] Getting alert counts for all folders');

    const endpoint = `${this.BASE_ENDPOINT}/counts/all-folders`;

    return this.get<AlertFolderCountsDto[]>(endpoint, this.requestConfig).pipe(
      tap((results) => {
        this.logger.info('[AlertsService] All folders alert counts retrieved', {
          foldersCount: results.length,
        });
      }),
      catchError((error) => {
        this.logger.error('[AlertsService] Failed to get all folders alert counts', { error });
        return of([]);
      })
    );
  }

  getAlertCounts(successionFileId: string): Observable<AlertCountsDto[]> {
    this.logger.info('[AlertsService] Getting alert counts for file', { successionFileId });

    const endpoint = `${this.BASE_ENDPOINT}/succession-file/${successionFileId}/counts`;

    return this.get<AlertCountsDto[]>(endpoint, this.requestConfig).pipe(
      tap((results) => {
        this.logger.info('[AlertsService] Alert counts retrieved', {
          successionFileId,
          categoriesCount: results.length,
        });
      }),
      catchError((error) => {
        this.logger.error('[AlertsService] Failed to get alert counts', { successionFileId, error });
        return of([]);
      })
    );
  }

  /**
   * Gets unread alerts for a specific succession file
   * Updates the store with the fetched alerts
   * @param successionFileId Succession file ID
   * @returns Observable of AlertDto array
   */
  getUnreadAlertsForFile(successionFileId: number): Observable<AlertDto[]> {
    this.logger.info('[AlertsService] Getting unread alerts for file', { successionFileId });

    const endpoint = `${this.BASE_ENDPOINT}/succession-file/${successionFileId}/unread`;

    return this.get<AlertDto[]>(endpoint, this.requestConfig).pipe(
      tap((alerts) => {
        this.logger.info('[AlertsService] Unread alerts retrieved', {
          successionFileId,
          count: alerts.length
        });
        // Update store with alerts
        alerts.forEach(alert => {
          const notification = this.mapAlertDtoToNotification(alert);
          const existing = this.notificationsStore.getNotificationById(notification.id);
          if (!existing) {
            this.notificationsStore.addNotification(notification);
          } else {
            this.updateNotificationInStore(notification);
          }
        });
      }),
      catchError((error) => {
        this.logger.error('[AlertsService] Failed to get unread alerts', { successionFileId, error });
        return of([]);
      })
    );
  }

  /**
   * Gets paginated unread alerts for a succession file
   * @param successionFileId Succession file ID
   * @param page Page number (default: 1)
   * @param pageSize Page size (default: 20)
   * @returns Observable of AlertDtoApiPaginated
   */
  searchUnreadAlertsForFile(
    successionFileId: number,
    page: number = 1,
    pageSize: number = 20
  ): Observable<AlertDtoApiPaginated> {
    this.logger.info('[AlertsService] Searching unread alerts', {
      successionFileId,
      page,
      pageSize
    });

    const queryParams = `?page=${page}&pageSize=${pageSize}`;
    const endpoint = `${this.BASE_ENDPOINT}/succession-file/${successionFileId}/unread/search${queryParams}`;

    return this.get<AlertDtoApiPaginated>(endpoint, this.requestConfig).pipe(
      tap((result) => {
        this.logger.info('[AlertsService] Paginated alerts retrieved', {
          successionFileId,
          page,
          totalItems: result.count || 0,
          itemsCount: result.data?.length || 0,
          status: result.status,
          message: result.message
        });
        // Update store with alerts
        if (result.data) {
          result.data.forEach(alert => {
            const notification = this.mapAlertDtoToNotification(alert);
            if (!this.notificationsStore.getNotificationById(notification.id)) {
              this.notificationsStore.addNotification(notification);
            }
          });
        }
      }),
      catchError((error) => {
        this.logger.error('[AlertsService] Failed to search unread alerts', {
          successionFileId,
          page,
          error
        });
        // Return empty paginated result
        return of({
          status: null,
          message: null,
          pagination: null,
          data: [],
          count: 0
        } as AlertDtoApiPaginated);
      })
    );
  }

  /**
   * Marks an alert as read for the current user
   * Updates the store immediately for optimistic UI update
   * @param id Alert ID (UUID)
   * @returns Observable that completes when successful
   */
  markAsRead(id: string): Observable<void> {
    this.logger.info('[AlertsService] Marking alert as read', { id });

    // Optimistic update - update store immediately
    this.notificationsStore.markAsRead(id);

    const endpoint = `${this.BASE_ENDPOINT}/${id}/read`;

    return this.post<void>(endpoint, {}, this.requestConfig).pipe(
      tap(() => {
        this.logger.info('[AlertsService] Alert marked as read successfully', { id });
        // Store already updated optimistically, but we could refresh from server if needed
      }),
      catchError((error) => {
        this.logger.error('[AlertsService] Failed to mark alert as read', { id, error });
        // Revert optimistic update on error
        // Note: We'd need to fetch the alert again to revert, or keep previous state
        // For now, we'll leave it as is since the API call might have succeeded
        // but network error occurred
        throw error;
      })
    );
  }

  /**
   * Marks multiple alerts as read
   * @param ids Array of alert IDs
   * @returns Observable that completes when all are marked as read
   */
  markMultipleAsRead(ids: string[]): Observable<void> {
    this.logger.info('[AlertsService] Marking multiple alerts as read', { count: ids.length });

    // Optimistic update
    ids.forEach(id => this.notificationsStore.markAsRead(id));

    // Call API for each (in a real scenario, you might have a bulk endpoint)
    const observables = ids.map(id =>
      this.post<void>(`${this.BASE_ENDPOINT}/${id}/read`, {}, this.requestConfig).pipe(
        catchError((error) => {
          this.logger.error('[AlertsService] Failed to mark alert as read', { id, error });
          return of(undefined); // Continue even if one fails
        })
      )
    );

    if (observables.length === 0) {
      return of(undefined);
    }

    return forkJoin(observables).pipe(
      tap(() => {
        this.logger.info('[AlertsService] All alerts marked as read', { count: ids.length });
      }),
      map(() => undefined)
    );
  }

  /**
   * Dismisses/deletes an alert for the current user
   * Updates the store immediately for optimistic UI update
   * @param id Alert ID (UUID)
   * @returns Observable that completes when successful
   */
  dismissAlert(id: string): Observable<void> {
    this.logger.info('[AlertsService] Dismissing alert', { id });

    // Store the notification before removing (for potential revert)
    const notification = this.notificationsStore.getNotificationById(id);

    // Optimistic update - remove from store immediately
    this.notificationsStore.removeNotification(id);

    const endpoint = `${this.BASE_ENDPOINT}/${id}/dismiss`;

    return this.post<void>(endpoint, {}, this.requestConfig).pipe(
      tap(() => {
        this.logger.info('[AlertsService] Alert dismissed successfully', { id });
        // Store already updated optimistically
      }),
      catchError((error) => {
        this.logger.error('[AlertsService] Failed to dismiss alert', { id, error });
        // Revert optimistic update on error
        if (notification) {
          this.notificationsStore.addNotification(notification);
        }
        throw error;
      })
    );
  }

  /**
   * Builds query string from filter parameters
   * @param filters Filter options
   * @returns Query string
   */
  private buildQueryParams(filters?: AlertsFiltersParams): string {
    if (!filters) {
      return '';
    }

    const params = new URLSearchParams();

    if (filters.onlyUnread !== undefined) {
      params.set('onlyUnread', String(filters.onlyUnread));
    }

    if (filters.includeDismissed !== undefined) {
      params.set('includeDismissed', String(filters.includeDismissed));
    }

    if (filters.successionFileId !== undefined) {
      params.set('successionFileId', String(filters.successionFileId));
    }

    if (filters.category !== undefined) {
      params.set('category', String(filters.category));
    }

    if (filters.severity !== undefined) {
      params.set('severity', String(filters.severity));
    }

    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }

  /**
   * Updates an existing notification in the store
   * Used when fetching alerts that might have changed (e.g., readAt status)
   * @param notification Updated notification
   */
  private updateNotificationInStore(notification: AppNotification): void {
    const currentState = this.notificationsStore.state();
    const notifications = currentState.notifications.map(n =>
      n.id === notification.id ? notification : n
    );

    // Recalculate unread notifications
    const unreadNotifications = notifications.filter(n => !n.readAt);
    const unreadCount = unreadNotifications.length;

    this.notificationsStore.setState({
      notifications,
      unreadNotifications,
      unreadCount,
    });
  }

  /**
   * Maps AlertDto to Notification interface
   * @param alertDto Alert DTO from API
   * @returns Notification object
   */
  private mapAlertDtoToNotification(alertDto: AlertDto): AppNotification {
    // Parse payload if present
    let parsedPayload: Record<string, unknown> | undefined;
    if (alertDto.payload) {
      try {
        parsedPayload = JSON.parse(alertDto.payload);
      } catch (error) {
        this.logger.warn('[AlertsService] Failed to parse alert payload', {
          alertId: alertDto.id,
          error
        });
      }
    }

    return {
      ...alertDto,
      parsedPayload,
      isProcessing: false,
    };
  }
}

